/**
 * IMPORTANT: This file is modified from the original shadcn/ui file.
 *            DO NOT OVERWRITE IT WITH THE CLI.
 */

import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { cn } from "@/utils/cn"
import { proseVariants } from "../content/prose"

const alertVariants = cva(
  [
    "relative flex w-full flex-col rounded-lg border px-5 pt-3 pb-[13px] text-foreground [&>svg]:absolute [&>svg]:text-inherit [&>svg~*]:pl-[28px]",
  ],
  {
    variants: {
      variant: {
        default: "bg-linear-to-bl from-foreground/5 via-foreground/5 to-background",
        success: "bg-linear-to-bl from-green-400/15 to-background [&>svg]:text-green-400",
        destructive: "bg-linear-to-bl from-destructive/10 to-background [&>svg]:text-destructive",
        warning: "bg-linear-to-bl from-warning/10 to-background [&>svg]:text-warning",
        discord:
          "bg-linear-to-bl from-[#6F86D3]/15 to-background saturate-150 [&>svg]:text-[#6F86D3]",
        info: "bg-linear-to-bl from-blue-400/15 to-background [&>svg]:text-blue-400",
      },
      size: {
        xs: "gap-1 px-3 pt-2.5 pb-2.5 [&>h5]:text-xs [&>p]:text-xs [&>p]:leading-normal [&>svg]:top-[10px] [&>svg]:left-[12px] [&>svg]:size-[14px] [&>svg~*]:pl-[22px]",
        sm: "gap-1 [&>h5]:text-sm [&>p]:text-xs [&>p]:leading-normal [&>svg]:top-[11px] [&>svg]:left-[16px] [&>svg]:size-[16px]",
        default:
          "gap-2 [&>p]:text-sm [&>p]:leading-normal [&>svg]:top-[11px] [&>svg]:left-[15px] [&>svg]:size-[18px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, size, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant, size }), className)}
    {...props}
  />
))
Alert.displayName = "Alert"

const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h5 ref={ref} className={cn("font-medium leading-none tracking-tight", className)} {...props} />
  ),
)
AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(proseVariants({ size: "sm" }), "text-foreground/75", className)}
    {...props}
  />
))
AlertDescription.displayName = "AlertDescription"

export { Alert, AlertTitle, AlertDescription }
